import { isEstimatedEntry, macroText, portionText } from "../domain/foodView";
import { MEAL_LABEL, totalsOf } from "../domain/intake";
import type { FoodEntry, MealSlot } from "../domain/types";
import { Button, IconButton } from "./Button";
import { Over } from "./Hero";
import { Kcal } from "./Estimate";
import { I } from "./icons";
import { NavRow } from "./Rows";
import { Tag } from "./Tag";

/**
 * One meal of one day (SNIPPETS §33, the logged day): a heading on the ground — the meal's name,
 * its totals, and Add — over the meal's own group of rows: what was eaten, then what was planned
 * and not yet eaten.
 *
 * The heading sits outside the group, never inside it at row size: as a row it read as one more
 * dish. An empty meal keeps its heading, drops the totals line, and its group is one row —
 * "Nothing yet", with Repeat when there is something to repeat. An eaten row opens the entry's
 * sheet (portion, oil, every nutrient, delete). Planned rows carry a "planned" tag, [Log it] and a
 * remove. An estimated dish's kcal carries "~" (§39); the meal's totals never do. Repeat is
 * offered only while the meal has nothing eaten, which is the moment it is wanted, and keeps a
 * second tap from logging the same meal twice.
 */
export function MealSection({
  meal,
  entries,
  planned,
  onAdd,
  onOpen,
  onLogPlanned,
  onRemovePlanned,
  repeatLabel,
  onRepeat,
  onFit,
  share,
}: {
  meal: MealSlot;
  /** the meal's share of the day's budget, once the day has a plan: the heading reads
   *  "Lunch · 558 of ~630" and a meal well past it says by how much (FoodPlannedDay, ADR-043) */
  share?: number;
  entries: FoodEntry[];
  planned: FoodEntry[];
  /** opens the picker with this meal preselected; absent while the food list is loading */
  onAdd?: () => void;
  /** opens a logged entry's sheet */
  onOpen: (e: FoodEntry) => void;
  onLogPlanned: (id: string) => void;
  onRemovePlanned: (id: string) => void;
  /** e.g. "Repeat yesterday" — shown with `onRepeat` while nothing is eaten */
  repeatLabel?: string;
  onRepeat?: () => void;
  /** scales the planned items to what is left of the day, when that is less than they add to */
  onFit?: () => void;
}) {
  const budgeted = typeof share === "number" && share > 0;
  const label = budgeted && meal === "snack" ? "Snack" : MEAL_LABEL[meal];
  const sub = totalsOf(entries);
  const plan = totalsOf(planned);
  const repeat = onRepeat && repeatLabel && !entries.length ? onRepeat : undefined;
  const empty = !entries.length && !planned.length;
  // Over by a tenth or more is said in the danger word; rounding and a spoon of oil are not "over".
  const over = budgeted && sub.kcal > share * 1.1 ? sub.kcal - share : 0;

  const summary = sub.items
    ? budgeted
      ? macroText(sub)
      : `${sub.kcal.toLocaleString()} kcal · ${macroText(sub)}`
    : plan.items
      ? `${plan.kcal.toLocaleString()} planned · ${macroText(plan)}`
      : null;
  const heading = budgeted ? `${label} · ${sub.kcal.toLocaleString()} of ~${share.toLocaleString()}` : label;

  return (
    <section className="k-food-meal" aria-label={label}>
      <div className="k-food-mealhead">
        <div className="k-food-mealhead__txt">
          <h3 className="k-food-mealhead__h">{heading}</h3>
          {summary ? (
            <p className="k-food-mealhead__sub">
              {over ? (
                <>
                  <Over>{over.toLocaleString()} over</Over> ·{" "}
                </>
              ) : null}
              {summary}
            </p>
          ) : null}
        </div>
        {onAdd ? (
          <Button variant="ghost" icon={I.plus} onClick={onAdd} ariaLabel={`Add to ${label.toLowerCase()}`}>
            Add
          </Button>
        ) : null}
      </div>

      <div className="k-rows">
        {empty ? (
          <div className="k-row k-food-empty">
            <span className="k-food-empty__txt">Nothing yet</span>
            {repeat ? (
              <Button variant="ghost" onClick={repeat}>
                {repeatLabel}
              </Button>
            ) : null}
          </div>
        ) : null}

        {entries.map((e) => (
          <NavRow
            key={e.id}
            // a food of the user's own reads as theirs here too, a dish they cook most of all
            title={e.code?.startsWith("cf:") ? <>{e.name} <Tag tone="yours" /></> : e.name}
            subtitle={`${portionText(e)} · ${macroText(e)}`}
            value={<Kcal kcal={e.kcal} estimated={isEstimatedEntry(e)} />}
            onClick={() => onOpen(e)}
          />
        ))}

        {planned.map((e) => (
          <NavRow
            key={e.id}
            kind="action"
            title={e.name}
            subtitle={
              <>
                <Tag tone="planned" /> {portionText(e)} · <Kcal kcal={e.kcal} estimated={isEstimatedEntry(e)} />
              </>
            }
            action={
              <span className="k-food-acts">
                <Button variant="ghost" onClick={() => onLogPlanned(e.id)} ariaLabel={`Log ${e.name}`}>
                  Log it
                </Button>
                <IconButton
                  icon={I.close}
                  label={`Remove ${e.name} from the plan`}
                  onClick={() => onRemovePlanned(e.id)}
                />
              </span>
            }
          />
        ))}

        {/* Repeat alongside a plan: the plan's rows are what is shown, so Repeat moves to the foot */}
        {(repeat && planned.length) || onFit ? (
          <div className="k-row k-food-mealacts">
            {repeat && planned.length ? (
              <Button variant="ghost" onClick={repeat}>
                {repeatLabel}
              </Button>
            ) : null}
            {onFit ? (
              <Button variant="ghost" onClick={onFit}>
                Fit plan to what&rsquo;s left
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
